/* eslint-disable react-hooks/exhaustive-deps */
import { Alert } from '@mui/material'
import MatAppBar from '../MatAppBar/MatAppBar'
import { useState, useEffect } from 'react'
import { connect, useSelector } from 'react-redux'
import { makeStyles, createStyles } from '@mui/styles'
import { setShouldRender } from '../ProgressOverlay/store/actionCreator'
import CacheListManager from '../CacheListManager/CacheListManager'
import {
  INPUT_PERFORMANCE_DELAY,
  SCHEDULE_OR_ASSIGNMENT,
  WORK_ORDER_FILTER_PROPERTIES,
  api,
} from '../../globalConstants'
import { fetchData } from '../../service/HttpService'
import { setPreventativeMaintenanceWorkorderList } from '../WorkOrderList/WorkOrderListStore/actionCreator'
import { setAssignments } from '../WorkOrderList/WorkOrderListStore/actionCreator'
import { useAuth } from '@praxis/component-auth'
import StatusAlert from '../StatusAlert/StatusAlert'
import CommonCardList from '../CommonCardList/CommonCardList'
import { filterAssignmentsBy7Days } from '../../utils'
import useSearch from '../../utils/hooks/useSearch'

const useStyles = makeStyles((theme) =>
  createStyles({
    cardHeader: {
      background: theme.palette.grey[300],
    },
    actionIcon: {
      margin: theme.spacing(1, 0.5, 0, 0),
    },
    cardContent: {
      padding: theme.spacing(1),
    },
    warningError: {
      margin: theme.spacing(1),
    },
  }),
)

const PreventativeMaintenanceWorkOrders = ({
  preventativeMaintenanceWorkorders,
  setShouldRender,
  lastUpdated,
  storeId,
  setPreventativeMaintenanceWorkorderList,
  setAssignments,
  toggle,
}) => {
  const URL_SEARCH_KEY = 'search'
  const classes = useStyles()
  const auth = useAuth()
  const userId = auth.session.userInfo.lanId?.toUpperCase()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const { isDCUser } = useSelector((state) => ({
    ...state.userInfoReducer,
  }))

  const [filteredWorkOrders, onSearch] = useSearch(
    URL_SEARCH_KEY,
    preventativeMaintenanceWorkorders || [],
    WORK_ORDER_FILTER_PROPERTIES(isDCUser),
    true,
    INPUT_PERFORMANCE_DELAY,
  )

  const preventativeMaintenanceWorkordersLength =
    preventativeMaintenanceWorkorders?.length

  useEffect(() => {
    return () => setPreventativeMaintenanceWorkorderList([])
  }, [])

  useEffect(() => {
    const getOpenWorkOrders = async () => {
      if (!preventativeMaintenanceWorkordersLength && !loading) {
        try {
          setShouldRender(true, 'Loading...', 'PM Work Orders')
          setLoading(true)
          setError(false)

          const response = await fetchData(api.openWorkOrders(storeId))
          setPreventativeMaintenanceWorkorderList(response.woDetails)
        } catch (error) {
          setError(true)
        } finally {
          setLoading(false)
          setShouldRender(false)
        }
      }
    }
    getOpenWorkOrders()
  }, [preventativeMaintenanceWorkordersLength])

  const infoMessage = `${filteredWorkOrders.length} / ${preventativeMaintenanceWorkordersLength} PM Work Order(s).`

  const getAssignments = async () => {
    try {
      setShouldRender(true, 'Loading...', SCHEDULE_OR_ASSIGNMENT(isDCUser))
      const response = await fetchData(api.myAssignments(storeId, userId))
      const assigmentsScheduledThisWeek = filterAssignmentsBy7Days(
        response.assignmentsdata,
      )
      setAssignments(assigmentsScheduledThisWeek)
    } catch (error) {
      setError(true)
    } finally {
      setLoading(false)
      setShouldRender(false)
    }
  }

  useEffect(() => {
    preventativeMaintenanceWorkordersLength && getAssignments()
  }, [toggle, preventativeMaintenanceWorkordersLength])

  return (
    <>
      <MatAppBar
        arrowBack
        text="PM Work Orders"
        search={{
          onSearch: (input) => onSearch(input),
          searchKey: URL_SEARCH_KEY,
        }}
      />
      {preventativeMaintenanceWorkordersLength > 0 && !loading && (
        <CacheListManager
          lastUpdated={lastUpdated}
          listLength={preventativeMaintenanceWorkordersLength}
          clearMethod={() => setPreventativeMaintenanceWorkorderList([])}
        />
      )}

      {!filteredWorkOrders?.length && !loading && !error && (
        <Alert
          severity="warning"
          data-testid={'no-data-text'}
          className={classes.warningError}
        >
          There are no preventive maintenance work orders to display.
        </Alert>
      )}
      {error && !loading && (
        <Alert severity="error" className={classes.warningError}>
          Unable to connect to preventive maintenance work orders service.
        </Alert>
      )}
      {filteredWorkOrders && !loading ? (
        <>
          <StatusAlert text={infoMessage} />
          <CommonCardList
            listData={filteredWorkOrders}
            filteredWorkOrdersLength={preventativeMaintenanceWorkordersLength}
          />
        </>
      ) : null}
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    preventativeMaintenanceWorkorders:
      state.workOrderListReducer.preventativeMaintenanceWorkorders,
    storeId: state.storeInfoReducer.storeId,
    lastUpdated:
      state.workOrderListReducer.lastUpdated.preventativeMaintenanceWorkorders,
    toggle: state.workOrderListReducer.toggle,
  }
}

const mapDispatchToProps = {
  setShouldRender,
  setPreventativeMaintenanceWorkorderList,
  setAssignments,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PreventativeMaintenanceWorkOrders)
